import { useState, useEffect, useRef } from "react";
import {
  getAllSectionBrands as getAllBrands,
  addSectionBrand as addBrand,
  updateBrand,
  deleteBrand,
  updateBrandSettings,
} from "../services/brandService";
import DashboardHeader from "../components/dashboard/DashboardHeader";
import { toggleSidebar } from "../utils/sidebarToggle";
import { useBrands } from "../context/BrandsContext";
import BrandSettingHeader from "../components/setting/PageHeader";

// ─── Config ───────────────────────────────────────────────────────────────────
const SECTIONS = [
  { key: "pc", label: "PC" },
  { key: "lab", label: "Lab", noBrands: true },
  { key: "bar", label: "Bar" },
  { key: "coin", label: "Coin" },
  { key: "ginni", label: "Ginni", noBrands: true },
  { key: "silver", label: "Silver" },
];

// ─── Toast ────────────────────────────────────────────────────────────────────
function Toast({ toasts }) {
  return (
    <div
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 99999,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        pointerEvents: "none",
      }}
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          style={{
            padding: "10px 18px",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 500,
            color: "#fff",
            background: t.type === "error" ? "#dc3545" : "#198754",
            boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
            animation: "toastIn 0.2s ease",
          }}
        >
          {t.message}
        </div>
      ))}
      <style>{`
        @keyframes toastIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0);   }
        }
      `}</style>
    </div>
  );
}

function useToast() {
  const [toasts, setToasts] = useState([]);
  const push = (message, type = "success") => {
    const id = Date.now();
    setToasts((p) => [...p, { id, message, type }]);
    setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), 3000);
  };
  return { toasts, push };
}

// ─── Confirm dialog ───────────────────────────────────────────────────────────
function ConfirmDialog({ message, onConfirm, onCancel, loading }) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9998,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        onClick={onCancel}
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(0,0,0,0.4)",
        }}
      />
      <div
        style={{
          position: "relative",
          background: "#fff",
          borderRadius: 10,
          padding: "1.5rem",
          width: "min(360px, 90vw)",
          boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: 36, marginBottom: 8 }}>⚠️</div>
        <p
          style={{
            fontSize: 14,
            color: "#333",
            marginBottom: "1.25rem",
            lineHeight: 1.5,
          }}
        >
          {message}
        </p>
        <div className="d-flex gap-2 justify-content-center">
          <button
            className="btn btn-secondary btn-sm"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="btn btn-danger btn-sm"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── No-brand notice ──────────────────────────────────────────────────────────
function NoBrandsNotice({ label }) {
  return (
    <div
      style={{
        border: "1px dashed #dee2e6",
        borderRadius: 8,
        padding: "2rem 1rem",
        textAlign: "center",
        background: "#fafafa",
      }}
    >
      <div style={{ fontSize: 28, marginBottom: 8 }}>🏷️</div>
      <p style={{ fontSize: 14, color: "#6c757d", marginBottom: 4 }}>
        <strong>{label}</strong> doesn't use brands.
      </p>
      <p style={{ fontSize: 12, color: "#adb5bd", marginBottom: 0 }}>
        This section tracks items without a brand identifier.
      </p>
    </div>
  );
}

function BrandSettingsModal({ section, brand, onClose, onSaved, toast }) {
  const [weights, setWeights] = useState(
    brand.weights?.length
      ? brand.weights.map((item) => ({
          weight:
            item.weight !== undefined && item.weight !== null
              ? String(item.weight)
              : "",
          making:
            item.making !== undefined && item.making !== null
              ? String(item.making)
              : "",
        }))
      : [{ weight: "", making: "" }],
  );

  const [saving, setSaving] = useState(false);

  const addWeight = () => {
    setWeights((prev) => [...prev, { weight: "", making: "" }]);
  };

  const removeWeight = (index) => {
    setWeights((prev) => {
      const updated = prev.filter((_, i) => i !== index);

      // Keep at least one row
      return updated.length ? updated : [{ weight: "", making: "" }];
    });
  };

  const updateWeight = (index, value) => {
    setWeights((prev) =>
      prev.map((item, i) => (i === index ? { ...item, weight: value } : item)),
    );
  };

  const updateMaking = (index, value) => {
    setWeights((prev) =>
      prev.map((item, i) => (i === index ? { ...item, making: value } : item)),
    );
  };

  const handleSave = async () => {
    // Check for empty weight fields
    const hasEmptyWeight = weights.some(
      (item) => String(item.weight).trim() === "",
    );

    if (hasEmptyWeight) {
      toast("Please enter a value for every weight.", "error");
      return;
    }

    // Convert weights to numbers
    const parsedWeights = weights.map((item) => ({
      weight: Number(item.weight),
      making:
        String(item.making).trim() === "" ? undefined : Number(item.making),
    }));

    // Check weights
    const invalidWeight = parsedWeights.some(
      (item) => !Number.isFinite(item.weight),
    );

    if (invalidWeight) {
      toast("All weights must be valid numbers.", "error");
      return;
    }

    // Check zero or negative weights
    const invalidPositiveWeight = parsedWeights.some(
      (item) => item.weight <= 0,
    );

    if (invalidPositiveWeight) {
      toast("Weights must be greater than 0.", "error");
      return;
    }

    // Prevent duplicate weights
    const uniqueWeights = new Set(parsedWeights.map((item) => item.weight));

    if (uniqueWeights.size !== parsedWeights.length) {
      toast("Duplicate weights are not allowed.", "error");
      return;
    }

    // Validate making
    const invalidMaking = parsedWeights.some(
      (item) =>
        item.making !== undefined &&
        (!Number.isFinite(item.making) || item.making <= 0),
    );

    if (invalidMaking) {
      toast("Making must be a valid number greater than 0.", "error");
      return;
    }

    setSaving(true);

    try {
      const res = await updateBrandSettings(section, brand._id, {
        weights: parsedWeights,
      });

      if (res.success) {
        onSaved({
          weights: parsedWeights,
        });

        toast("Brand settings updated.");
        onClose();
      } else {
        toast(res.message || "Failed to update settings.", "error");
      }
    } catch (err) {
      console.log(err);

      const message = err.response?.data?.message || "Network error.";

      toast(message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1050,
        padding: 15,
      }}
    >
      <div
        style={{
          background: "#fff",
          width: "100%",
          maxWidth: 550,
          borderRadius: 8,
          boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "14px 18px",
            borderBottom: "1px solid #dee2e6",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              Brand Settings
            </div>

            <div
              style={{
                fontSize: 12,
                color: "#6c757d",
                marginTop: 2,
              }}
            >
              {brand.name}
            </div>
          </div>

          <button
            type="button"
            className="btn-close"
            onClick={onClose}
            disabled={saving}
          />
        </div>

        {/* Body */}
        <div style={{ padding: 18 }}>
          <label
            className="form-label"
            style={{
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            Weights & Making
          </label>

          {weights.map((item, index) => (
            <div
              key={index}
              className="d-flex gap-2 mb-2"
              style={{
                alignItems: "center",
              }}
            >
              {/* Weight */}
              <div style={{ flex: 1 }}>
                <input
                  type="number"
                  min="0"
                  step="any"
                  className="form-control"
                  placeholder="Weight e.g. 10"
                  value={item.weight}
                  onChange={(e) => updateWeight(index, e.target.value)}
                  disabled={saving}
                />
              </div>

              {/* Making */}
              <div style={{ flex: 1 }}>
                <input
                  type="number"
                  min="0"
                  step="any"
                  className="form-control"
                  placeholder="Making e.g. 500"
                  value={item.making}
                  onChange={(e) => updateMaking(index, e.target.value)}
                  disabled={saving}
                />
              </div>

              {/* Remove */}
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={() => removeWeight(index)}
                disabled={saving}
                style={{
                  width: 40,
                  flexShrink: 0,
                }}
              >
                ✕
              </button>
            </div>
          ))}

          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={addWeight}
            disabled={saving}
          >
            + Add Weight
          </button>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "12px 18px",
            borderTop: "1px solid #dee2e6",
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>

          <button
            type="button"
            className="btn btn-warning btn-sm"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save Settings"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Inline editable brand row ────────────────────────────────────────────────
function BrandRow({
  brand,
  sectionKey,
  onUpdated,
  onDeleted,
  toast,
  refreshBrands,
}) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(brand.name);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const hasWeightMaking = sectionKey === "bar" || sectionKey === "coin";

  const inputRef = useRef(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const cancelEdit = () => {
    setEditing(false);
    setVal(brand.name);
  };

  const handleSave = async () => {
    const trimmed = val.trim();
    if (!trimmed) return;
    if (trimmed === brand.name) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const res = await updateBrand(sectionKey, brand._id, trimmed);
      if (res.success) {
        onUpdated(brand._id, trimmed);
        toast(`Renamed to "${trimmed}"`);
        setEditing(false);
      } else {
        toast(res.message || "Failed to rename.", "error");
      }
    } catch (err) {
      const errorResponseMessage =
        err.response?.data?.message || "Network error.";
      toast(errorResponseMessage, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await deleteBrand(sectionKey, brand._id);
      if (res.success) {
        onDeleted(brand._id);
        toast(`"${brand.name}" deleted.`);
      } else {
        toast(res.message || "Failed to delete.", "error");
      }
    } catch (err) {
      const errorResponseMessage =
        err.response?.data?.error || "Network error.";
      toast(errorResponseMessage, "error");
    } finally {
      setDeleting(false);
      setConfirm(false);
    }
  };

  return (
    <>
      {confirm && (
        <ConfirmDialog
          message={`Delete "${brand.name}"? This won't affect existing transactions.`}
          onConfirm={handleDelete}
          onCancel={() => setConfirm(false)}
          loading={deleting}
        />
      )}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 12px",
          borderBottom: "1px solid #f0f0f0",
          background: editing ? "#fffdf2" : "#fff",
          transition: "background 0.15s",
        }}
      >
        <div
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: "#0d6efd",
            flexShrink: 0,
          }}
        />

        {editing ? (
          <>
            <input
              ref={inputRef}
              type="text"
              className="form-control form-control-sm"
              value={val}
              onChange={(e) => setVal(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSave();
                if (e.key === "Escape") cancelEdit();
              }}
              style={{ flex: 1 }}
              disabled={saving}
            />
            <button
              className="btn btn-warning btn-sm"
              style={{
                fontSize: 12,
                padding: "2px 10px",
                whiteSpace: "nowrap",
              }}
              onClick={handleSave}
              disabled={saving || !val.trim()}
            >
              {saving ? "…" : "Save"}
            </button>
            <button
              className="btn btn-secondary btn-sm"
              style={{ fontSize: 12, padding: "2px 8px" }}
              onClick={cancelEdit}
              disabled={saving}
            >
              ✕
            </button>
          </>
        ) : (
          <>
            <span style={{ flex: 1, fontSize: 14, color: "#212529" }}>
              {brand.name}
            </span>
            {hasWeightMaking && (
              <button
                className="btn btn-outline-primary btn-sm"
                style={{
                  fontSize: 12,
                  padding: "2px 10px",
                }}
                onClick={() => setShowSettings(true)}
              >
                Settings
              </button>
            )}
            <button
              className="btn btn-warning btn-sm"
              style={{ fontSize: 12, padding: "2px 10px" }}
              onClick={() => setEditing(true)}
            >
              Edit
            </button>

            <button
              className="btn btn-danger btn-sm"
              style={{ fontSize: 12, padding: "2px 8px" }}
              onClick={() => setConfirm(true)}
            >
              ✕
            </button>
          </>
        )}
      </div>

      {showSettings && (
        <BrandSettingsModal
          section={sectionKey}
          brand={brand}
          onClose={() => setShowSettings(false)}
          onSaved={(settings) => {
            refreshBrands();
          }}
          toast={toast}
        />
      )}
    </>
  );
}

// ─── Section panel ────────────────────────────────────────────────────────────
function SectionPanel({ section, toast, refresh }) {
  const { brands: storedBrands } = useBrands();
  const [brands, setBrands] = useState([]);
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState("");
  const inputRef = useRef(null);

  // sync from context
  useEffect(() => {
    if (storedBrands?.length > 0) {
      const sect = storedBrands.find((s) => s.section === section.key);
      // console.log({ sect });
      setBrands(sect?.brands ?? []);
    }
  }, [storedBrands, section.key]);

  const handleAdd = async () => {
    const trimmed = newName.trim();
    if (!trimmed) {
      setAddError("Enter a brand name.");
      return;
    }
    if (brands.some((b) => b.name.toLowerCase() === trimmed.toLowerCase())) {
      setAddError("Brand already exists.");
      return;
    }
    setAdding(true);
    setAddError("");
    try {
      const res = await addBrand(section.key, trimmed);
      if (res.success) {
        setBrands((p) => [...p, res.brand]);
        setNewName("");
        toast(`"${trimmed}" added to ${section.label}.`);
        inputRef.current?.focus();
        refresh?.();
      } else {
        setAddError(res.message || "Failed to add.");
      }
    } catch (err) {
      const errorResponseMessage =
        err.response?.data?.message || "Network error.";
      setAddError(errorResponseMessage);
    } finally {
      setAdding(false);
    }
  };

  const handleUpdated = (id, newBrandName) => {
    setBrands((p) =>
      p.map((b) => (b._id === id ? { ...b, name: newBrandName } : b)),
    );
    refresh?.();
  };

  const handleDeleted = (id) => {
    refresh?.();
  };

  if (section.noBrands) return <NoBrandsNotice label={section.label} />;

  return (
    <div>
      {/* add row */}
      <div className="row g-2 mb-3 align-items-start">
        <div className="col">
          <input
            ref={inputRef}
            type="text"
            className={`form-control form-control-sm ${addError ? "is-invalid" : ""}`}
            placeholder={`New ${section.label} brand…`}
            value={newName}
            onChange={(e) => {
              setNewName(e.target.value);
              setAddError("");
            }}
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            disabled={adding}
          />
          {addError && (
            <div className="invalid-feedback d-block" style={{ fontSize: 11 }}>
              {addError}
            </div>
          )}
        </div>
        <div className="col-auto">
          <button
            className="btn btn-warning btn-sm"
            onClick={handleAdd}
            disabled={adding || !newName.trim()}
            style={{ whiteSpace: "nowrap" }}
          >
            {adding ? "Adding…" : "+ Add Brand"}
          </button>
        </div>
      </div>

      {/* brand list */}
      <div
        style={{
          border: "1px solid #dee2e6",
          borderRadius: 6,
          overflow: "hidden",
          background: "#fff",
        }}
      >
        {brands.length === 0 ? (
          <p
            className="text-muted text-center py-3 mb-0"
            style={{ fontSize: 13 }}
          >
            No brands yet. Add one above.
          </p>
        ) : (
          brands.map((brand) => (
            <BrandRow
              key={brand._id}
              brand={brand}
              sectionKey={section.key}
              onUpdated={handleUpdated}
              onDeleted={handleDeleted}
              toast={toast}
              refreshBrands={refresh}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function BrandSettings() {
  const [activeKey, setActiveKey] = useState(SECTIONS[0].key);
  const { toasts, push: toast } = useToast();
  const { brands: storedBrands, refreshBrands: refresh } = useBrands();

  const activeSection = SECTIONS.find((s) => s.key === activeKey);

  const getBrandCount = (key) => {
    const sect = storedBrands?.find((s) => s.section === key);
    return sect?.brands?.length ?? 0;
  };

  return (
    <>
      <BrandSettingHeader toggleSidebar={toggleSidebar} />
      <div className="container mt-2" id="dashboardRoot">
        <Toast toasts={toasts} />

        {/* page header */}
        {/* <div className="mb-4">
          <h4 className="fw-bold mb-1" style={{ fontSize: 18 }}>
            Brand Settings
          </h4>
          <p className="text-muted mb-0" style={{ fontSize: 13 }}>
            Manage brands for each transaction section.
          </p>
        </div> */}

        {/* section tabs */}
        <div
          className="d-flex flex-wrap gap-2 mb-3 p-2 rounded"
          style={{ background: "#f8f9fa", border: "1px solid #dee2e6" }}
        >
          {SECTIONS.map((s) => {
            const isActive = activeKey === s.key;
            const count = getBrandCount(s.key);
            return (
              <button
                key={s.key}
                type="button"
                className={`btn btn-sm ${isActive ? "btn-primary" : "btn-outline-secondary"}`}
                style={{
                  fontSize: 13,
                  borderRadius: 20,
                  padding: "3px 16px",
                  position: "relative",
                }}
                onClick={() => setActiveKey(s.key)}
              >
                {s.label}
                {s.noBrands ? (
                  /* grey N/A pill */
                  <span
                    style={{
                      position: "absolute",
                      top: -6,
                      right: -4,
                      fontSize: 9,
                      fontWeight: 700,
                      background: "#6c757d",
                      color: "#fff",
                      borderRadius: 4,
                      padding: "1px 4px",
                      lineHeight: 1.4,
                    }}
                  >
                    N/A
                  </span>
                ) : (
                  count > 0 && (
                    /* brand count bubble */
                    <span
                      style={{
                        position: "absolute",
                        top: -6,
                        right: -4,
                        fontSize: 9,
                        fontWeight: 700,
                        minWidth: 16,
                        textAlign: "center",
                        background: isActive ? "#fff" : "#0d6efd",
                        color: isActive ? "#0d6efd" : "#fff",
                        borderRadius: 10,
                        padding: "1px 5px",
                        lineHeight: 1.4,
                      }}
                    >
                      {count}
                    </span>
                  )
                )}
              </button>
            );
          })}
        </div>

        {/* active section card */}
        {activeSection && (
          <div
            style={{
              background: "#fff",
              border: "1px solid #dee2e6",
              borderRadius: 8,
              overflow: "hidden",
            }}
          >
            {/* card header */}
            <div
              style={{
                padding: "12px 16px",
                borderBottom: "1px solid #dee2e6",
                background: "#f8f9fa",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <span style={{ fontWeight: 700, fontSize: 15, color: "#212529" }}>
                {activeSection.label}
              </span>
              {activeSection.noBrands ? (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    background: "#e9ecef",
                    color: "#6c757d",
                    border: "1px solid #dee2e6",
                    borderRadius: 6,
                    padding: "1px 8px",
                  }}
                >
                  No brands
                </span>
              ) : (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 500,
                    background: "#dbe8ff",
                    color: "#0d6efd",
                    border: "1px solid #c2d3f8",
                    borderRadius: 6,
                    padding: "1px 8px",
                  }}
                >
                  {getBrandCount(activeSection.key)} brand
                  {getBrandCount(activeSection.key) !== 1 ? "s" : ""}
                </span>
              )}
            </div>

            {/* card body */}
            <div style={{ padding: "16px" }}>
              <SectionPanel
                key={activeSection.key}
                section={activeSection}
                toast={toast}
                refresh={refresh}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
}
